/**
 * Image menu item and upload functionality for ProseMirror
 *
 * Provides a menu item for inserting images and handles the upload overlay
 * display during image uploads.
 */

import { MenuItem } from "prosemirror-menu";
import { EditorState, TextSelection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { schema } from "../adapter/schema";

import { collaborationUploadFile } from "api/api-worksheets";
import { t } from "i18n/i18n";
import { toolbarImageIcon } from "mds/utils/toolbar-icons";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { rollbarAndLogError } from "utils/logger";

/**
 * Display the overlay while image is uploading
 *
 * @param wrapperElement - The wrapper element containing the editor
 * @returns The overlay element, or null if it couldn't be created
 */
function addUploadOverlay(wrapperElement: HTMLElement): HTMLElement | null {
  let overlayElement = wrapperElement.querySelector<HTMLElement>(".prosemirror-upload-overlay");
  if (!overlayElement) {
    overlayElement = document.createElement("div");
    overlayElement.className =
      "prosemirror-upload-overlay " +
      "animate-fade-out animate-fade-out-trigger " +
      "pointer-events-auto " +
      "absolute bottom-0 left-0 right-0 top-0 " +
      "flex items-center justify-center " +
      "text-white bg-black/70 rounded-md " +
      "z-[1001]"; // Above the toolbar

    const text = document.createElement("p");
    text.className = "body";
    text.textContent = t("worksheet.rich_text_editor.uploading_image");
    overlayElement.appendChild(text);

    wrapperElement.appendChild(overlayElement);
  }

  return overlayElement;
}

/**
 * Create a menu item for inserting images
 *
 * When clicked, opens the browser's file picker, uploads the selected image,
 * and inserts it at the current cursor position.
 *
 * @returns A MenuItem for image insertion
 */
export function imageItem(): MenuItem {
  return new MenuItem({
    title: t("worksheet.rich_text_editor.insert_image"),
    icon: toolbarImageIcon,
    run: (state: EditorState, dispatch, view: EditorView) => {
      const wrapperElement = view.dom.closest(".prosemirror-editor-wrapper") as HTMLElement | null;
      if (!wrapperElement) {
        rollbarAndLogError("[ProseMirrorEditor] Wrapper element not found for image upload");
        return;
      }

      // Create a hidden file input element
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.style.display = "none";

      const cleanup = () => {
        if (input.parentNode) {
          input.parentNode.removeChild(input);
        }
      };

      const handleFileSelect = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];
        if (!file) {
          cleanup();
          return;
        }

        const overlay = addUploadOverlay(wrapperElement);

        try {
          // Upload the file
          const response = await collaborationUploadFile(file);

          // Get the current state (may have changed since button was clicked)
          const currentState = view.state;
          const currentFrom = currentState.selection.from;

          // Insert the image at the current selection position
          const imageNode = schema.nodes.image.create({ src: response.url });
          const tr = currentState.tr.insert(currentFrom, imageNode);

          // Move cursor after the inserted image (image node has size 1)
          const newPos = currentFrom + 1;
          const resolvedPos = tr.doc.resolve(newPos);
          tr.setSelection(TextSelection.near(resolvedPos));

          view.dispatch(tr);
        } catch (error) {
          rollbarAndLogError("[ProseMirrorEditor] Error uploading image", error);
          toastLocalizedOperationCatcher("image_upload_failure")(error);
        } finally {
          overlay?.remove();
          cleanup();
        }
      };

      input.addEventListener("change", handleFileSelect, { once: true });

      // Handle window focus to clean up if user cancels (closes file picker)
      const handleFocus = () => {
        // Small delay to allow change event to fire if file was selected
        setTimeout(() => {
          if (input.parentNode && (!input.files || input.files.length === 0)) {
            cleanup();
          }
        }, 100);
      };
      window.addEventListener("focus", handleFocus, { once: true });

      // Append to wrapper temporarily and trigger click
      wrapperElement.appendChild(input);
      input.click();
    },
  });
}
